import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type RouteComponentProps = {
    history: {
        length: number;
        action: 'POP' | 'PUSH' | 'REPLACE';
        location: ReturnType<typeof useLocation>;
        push: (to: string | { pathname?: string; search?: string; hash?: string; state?: unknown }, state?: unknown) => void;
        replace: (to: string | { pathname?: string; search?: string; hash?: string; state?: unknown }, state?: unknown) => void;
        go: (n: number) => void;
        goBack: () => void;
        goForward: () => void;
        block: (prompt?: boolean | string | ((location: { pathname: string; search: string }) => void)) => () => void;
        listen: (listener: () => void) => () => void;
        createHref: (to: string | { pathname?: string; search?: string; hash?: string }) => string;
    };
    location: ReturnType<typeof useLocation>;
    match: {
        params: ReturnType<typeof useParams>;
        isExact: boolean;
        path: string;
        url: string;
    };
};

export function withRouter<P extends object>(
    Component: React.ComponentType<P & RouteComponentProps>
): React.FC<P> {
    function ComponentWithRouter(props: P) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();

        const history = React.useMemo(
            () => ({
                length: 1,
                action: 'POP' as const,
                location,
                push: (to: string | { pathname?: string; search?: string; hash?: string; state?: unknown }, state?: unknown) => {
                    if (typeof to === 'string') {
                        navigate(to, { state });
                    } else {
                        navigate(
                            {
                                pathname: to.pathname ?? '',
                                search: to.search ?? '',
                                hash: to.hash ?? '',
                            },
                            { state: to.state ?? state }
                        );
                    }
                },
                replace: (to: string | { pathname?: string; search?: string; hash?: string; state?: unknown }, state?: unknown) => {
                    if (typeof to === 'string') {
                        navigate(to, { replace: true, state });
                    } else {
                        navigate(
                            {
                                pathname: to.pathname ?? '',
                                search: to.search ?? '',
                                hash: to.hash ?? '',
                            },
                            { replace: true, state: to.state ?? state }
                        );
                    }
                },
                go: (n: number) => navigate(n),
                goBack: () => navigate(-1),
                goForward: () => navigate(1),
                block: () => () => {},
                listen: () => () => {},
                createHref: (to: string | { pathname?: string; search?: string; hash?: string }) =>
                    typeof to === 'string'
                        ? to
                        : `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`,
            }),
            [location, navigate]
        );

        const match = {
            params,
            isExact: true,
            path: location.pathname,
            url: location.pathname,
        };

        return <Component {...(props as P)} history={history} location={location} match={match} />;
    }

    const name = Component.displayName || Component.name || 'Component';
    ComponentWithRouter.displayName = `withRouter(${name})`;

    return ComponentWithRouter;
}
